// src/app/modules/Devices/components/CommandsTab.jsx
import React, { useState } from 'react';
import { ListChecks, Info } from 'lucide-react';
import { toast } from 'react-toastify';
import { devicesApi, errorMessage } from '../api';
import { usePolling, useEvent, lastSeen, shortDate, COMMAND_LABELS } from '../utils';
import { Pill, EmptyState, Button, Spinner } from './ui';

const REFRESH_MS = 5000;

const statusPill = (c) => {
  if (c.status === 'done') return <Pill tone="success">Done</Pill>;
  if (c.status === 'cancelled') return <Pill>Cancelled</Pill>;
  if (c.failure_count > 0) return <Pill tone="danger">Failing</Pill>;
  return <Pill tone="warning">{c.delivered_count ? 'Sent, awaiting result' : 'Waiting for device'}</Pill>;
};

export function CommandsTab({ orgId, device, canManage, onChanged }) {
  const [rows, setRows] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useEvent(async () => {
    try {
      const res = await devicesApi.commands(orgId, device.device_id, { limit: 50 });
      setRows(res.commands);
    } catch (err) {
      if (rows === null) setRows([]);
    }
  });
  usePolling(load, REFRESH_MS);

  const cancel = async (c) => {
    setCancelling(c.command_id);
    try {
      await devicesApi.cancelCommand(orgId, device.device_id, c.command_id);
      toast.success('Command cancelled — the fingerprint stays active');
      load();
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setCancelling(null);
    }
  };

  if (rows === null) return <div className="dv-card__body" style={{ textAlign: 'center' }}><Spinner /></div>;

  const hasPending = rows.some((c) => c.status === 'pending');
  return (
    <>
      {hasPending && (
        <div className="dv-card__body" style={{ paddingBottom: 0 }}>
          <div className="dv-notice dv-notice--brand">
            <Info size={16} />
            <span>The device only picks up commands when someone presses its button. Pending commands run on the next press.</span>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState icon={ListChecks} title="No commands yet">Deleting fingerprints creates commands that the device runs on its next button press.</EmptyState>
      ) : (
        <div className="dv-table-wrap">
          <table className="dv-table">
            <thead>
              <tr><th>Command</th><th>Status</th><th>Sent to device</th><th>Created</th>{canManage && <th />}</tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.command_id}>
                  <td>
                    <div className="dv-strong">{(COMMAND_LABELS[c.type] || (() => c.type))(c)}</div>
                    <div className="dv-sub">{c.command_id}</div>
                  </td>
                  <td>
                    {statusPill(c)}
                    {c.failure_count > 0 && <div className="dv-sub">{c.failure_count} failed attempt{c.failure_count > 1 ? 's' : ''}{c.last_error ? ` · ${c.last_error}` : ''}</div>}
                  </td>
                  <td>{c.delivered_count ? `${c.delivered_count}× · ${lastSeen(c.last_delivered_at)}` : <span className="dv-sub">Not yet</span>}</td>
                  <td>{shortDate(c.created_at)}</td>
                  {canManage && (
                    <td className="dv-actions">
                      {c.status === 'pending' && <Button size="sm" variant="ghost" loading={cancelling === c.command_id} onClick={() => cancel(c)}>Cancel</Button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default CommandsTab;
